import React, { useState } from 'react';
import { Form, Input, InputNumber, Select, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface SmartFormFieldProps {
  name: string;
  label: string;
  type: 'input' | 'textarea' | 'number' | 'select';
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  options?: Array<{ label: string; value: string | number }>;
  min?: number;
  max?: number;
  rows?: number;
  validation?: {
    pattern?: RegExp;
    message?: string;
  };
}

const SmartFormField: React.FC<SmartFormFieldProps> = ({
  name,
  label,
  type,
  required = false,
  placeholder,
  helpText,
  options = [],
  min,
  max,
  rows = 3,
  validation,
}) => {
  const [focused, setFocused] = useState(false);

  // 构建验证规则
  const rules = [
    ...(required ? [{ required: true, message: `请输入${label}` }] : []),
    ...(validation
      ? [{ pattern: validation.pattern, message: validation.message }]
      : []),
  ];

  // 渲染不同类型的输入组件
  const renderInputComponent = () => {
    switch (type) {
      case 'textarea':
        return (
          <TextArea
            placeholder={placeholder}
            rows={rows}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              transition: 'all 0.2s ease',
              borderColor: focused ? '#10b981' : undefined,
            }}
          />
        );

      case 'number':
        return (
          <InputNumber
            placeholder={placeholder}
            min={min}
            max={max}
            style={{ width: '100%' }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
          />
        );

      case 'select':
        return (
          <Select
            placeholder={placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              transition: 'all 0.2s ease',
            }}
          >
            {options.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      default:
        return (
          <Input
            placeholder={placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              transition: 'all 0.2s ease',
              borderColor: focused ? '#10b981' : undefined,
            }}
          />
        );
    }
  };

  return (
    <Form.Item
      name={name}
      label={
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              color: focused ? '#10b981' : '#1f2937',
              fontWeight: focused ? '600' : '500',
              transition: 'all 0.2s ease',
            }}
          >
            {label}
          </span>
          {required && (
            <span style={{ color: '#ef4444', fontSize: '12px' }}>*</span>
          )}
          {helpText && (
            <Tooltip title={helpText} placement="top">
              <QuestionCircleOutlined
                style={{
                  color: '#6b7280',
                  fontSize: '12px',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          )}
        </div>
      }
      rules={rules}
      style={{
        marginBottom: '20px',
      }}
    >
      <div
        style={{
          position: 'relative',
          transition: 'all 0.2s ease',
          transform: focused ? 'translateY(-1px)' : 'translateY(0)',
        }}
      >
        {renderInputComponent()}

        {/* 智能提示 */}
        {focused && helpText && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 6px 6px',
              padding: '8px 12px',
              fontSize: '12px',
              color: '#6b7280',
              zIndex: 10,
              animation: 'slideDown 0.2s ease',
            }}
          >
            💡 {helpText}
          </div>
        )}
      </div>
    </Form.Item>
  );
};

export default SmartFormField;
